import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { KYCFormData, KYCSection } from '../../types/kyc.types';
import { KYCStatus } from '../../types/kyc.constants';
import { useDebounce } from '../../hooks/useDebounce';
import { toast } from 'sonner';
import kycService from '../../services/kycService';
import { useKYCBridge } from '../../stores/kycBridge';
import type { KYCReviewComment } from '../../types/complianceWorkflow';

interface KYCWizardContextType {
  // Client identification (TASK-220: Exposed for document extraction)
  clientId: string;

  // Current step management
  currentStep: number;
  totalSteps: number;
  setCurrentStep: (step: number) => void;

  // Form data
  formData: Partial<KYCFormData>;
  updateFormData: (section: KYCSection, data: any) => void;
  // TASK-322: Update top-level form fields (not nested in a section)
  updateTopLevelFields: (fields: Partial<KYCFormData>) => void;

  // TASK-261: Prefill tracking - version increments when data is prefilled from extraction
  // Step components watch this to re-initialize their forms
  prefillVersion: number;
  triggerPrefill: () => void;

  // Validation
  errors: Record<string, any>;
  validateStep: (step: number) => Promise<boolean>;
  clearErrors: (section: KYCSection) => void;

  // Step completion tracking
  completedSteps: Set<number>;
  markStepComplete: (step: number) => void;
  markStepIncomplete: (step: number) => void;

  // Navigation
  canNavigateToStep: (step: number) => boolean;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToStep: (step: number) => void;

  // Save functionality
  isSaving: boolean;
  lastSavedAt: Date | null;
  isDirty: boolean;
  saveProgress: (showNotification?: boolean) => Promise<void>;

  // Auto-save
  enableAutoSave: boolean;
  setEnableAutoSave: (enable: boolean) => void;

  // Submission
  isSubmitting: boolean;
  submitForm: () => Promise<void>;

  // Reset
  resetWizard: () => void;

  // Review mode
  reviewMode: boolean;
  reviewedSteps: Set<number>;
  markStepReviewed: (step: number) => void;
  allStepsReviewed: () => boolean;

  // TASK-373: Extraction proposal application
  applyExtractionProposal: (fieldPath: string, value: any) => void;

  // TASK-373: Embedded mode flag
  embedded: boolean;

  // CO review comments (for advisor view of changes_requested feedback)
  reviewComments: KYCReviewComment[];

  // True when profile status is changes_requested (enables re-submission flow)
  isChangesRequested: boolean;
}

const KYCWizardContext = createContext<KYCWizardContextType | undefined>(undefined);

export const useKYCWizard = () => {
  const context = useContext(KYCWizardContext);
  if (!context) {
    throw new Error('useKYCWizard must be used within KYCWizardProvider');
  }
  return context;
};

interface KYCWizardProviderProps {
  children: ReactNode;
  clientId: string;
  initialData?: Partial<KYCFormData>;
  onComplete?: (data: KYCFormData) => void;
  /** Review mode - relaxes validation, allows submission with incomplete data */
  reviewMode?: boolean;
  /** TASK-373: Embedded mode for KYC workspace (no page chrome) */
  embedded?: boolean;
  /** CO review comments for advisor view */
  reviewComments?: KYCReviewComment[];
  /** True when profile is in changes_requested status */
  isChangesRequested?: boolean;
}

// TASK-373: Step configuration — DocumentUpload removed (now handled in KYCWorkspace)
const KYC_STEPS = [
  { id: 1, name: 'Personal Information', section: 'personalInfo' as KYCSection },
  { id: 2, name: 'Contact Details', section: 'contactInfo' as KYCSection },
  { id: 3, name: 'Employment Information', section: 'employmentInfo' as KYCSection },
  { id: 4, name: 'Personal Background', section: 'personalBackground' as KYCSection },
  { id: 5, name: 'Tax Information', section: 'taxInfo' as KYCSection },
  { id: 6, name: 'Adverse Media & PEP', section: 'adverseMedia' as KYCSection },
  { id: 7, name: 'Business Activities', section: 'businessActivities' as KYCSection },
  { id: 8, name: 'Wealth Sources', section: 'wealthSources' as KYCSection },
  { id: 9, name: 'Income Sources', section: 'incomeSources' as KYCSection },
  { id: 10, name: 'Review & Submit', section: 'review' as KYCSection },
];


/**
 * Normalize proposal values to match form field expectations.
 * - Dates: convert various formats to YYYY-MM-DD for <input type="date">
 * - Enums: lowercase to match <select> option values
 */
function normalizeFieldValue(fieldPath: string, value: any): any {
  if (typeof value !== 'string' || !value) return value;

  const lastPart = fieldPath.split('.').pop() || '';

  // Date fields → convert to YYYY-MM-DD
  if (lastPart === 'dateOfBirth' || lastPart === 'date_of_birth' || lastPart.toLowerCase().includes('date')) {
    const parsed = parseDateToISO(value);
    if (parsed) return parsed;
  }

  // Explicit value maps for fields where backend/LLM values differ from form enums
  const VALUE_MAPS: Record<string, Record<string, string>> = {
    employmentStatus: {
      'employed': 'employed',
      'self-employed': 'selfEmployed',
      'self_employed': 'selfEmployed',
      'selfemployed': 'selfEmployed',
      'unemployed': 'unemployed',
      'retired': 'retired',
      'student': 'student',
      'other': 'other',
    },
    preferredContactMethod: {
      'email': 'email',
      'phone': 'phone',
      'whatsapp': 'whatsapp',
      'video_call': 'video_call',
      'video call': 'video_call',
      'videocall': 'video_call',
      'in_person': 'in_person',
      'in person': 'in_person',
      'inperson': 'in_person',
    },
    educationLevel: {
      'high school': 'high_school',
      'high_school': 'high_school',
      'highschool': 'high_school',
      'bachelor': 'bachelor',
      "bachelor's": 'bachelor',
      'bachelors': 'bachelor',
      "bachelor's degree": 'bachelor',
      'master': 'master',
      "master's": 'master',
      'masters': 'master',
      "master's degree": 'master',
      'mba': 'professional_degree',
      'phd': 'phd',
      'doctorate': 'phd',
      'professional_degree': 'professional_degree',
      'professional degree': 'professional_degree',
      'other': 'other',
    },
    spousePartnerEmploymentStatus: {
      'employed': 'employed',
      'self-employed': 'selfEmployed',
      'self_employed': 'selfEmployed',
      'selfemployed': 'selfEmployed',
      'unemployed': 'unemployed',
      'retired': 'retired',
      'homemaker': 'homemaker',
      'other': 'other',
    },
  };

  if (VALUE_MAPS[lastPart]) {
    const normalized = value.toLowerCase().replace(/[\s-]+/g, '_').replace(/_+/g, '_');
    // Try exact match first, then normalized match, then raw lowercase
    return VALUE_MAPS[lastPart][value.toLowerCase()]
      ?? VALUE_MAPS[lastPart][normalized]
      ?? value.toLowerCase();
  }

  // Boolean fields → convert to actual boolean
  const BOOLEAN_FIELDS = [
    'isPEP', 'isRCA', 'usPerson', 'taxExempt', 'insiderFlag',
    'hasAdverseMedia', 'sanctionsScreened', 'sanctionsHit',
    'hasBusinessActivities',
    'inheritanceExpected', 'unusualTransactions',
    'expectedIncomeChange', 'retirementIncome', 'otherFinancialSupport',
  ];
  if (BOOLEAN_FIELDS.includes(lastPart)) {
    if (typeof value === 'boolean') return value;
    return value.toLowerCase() === 'true' || value === '1' || value.toLowerCase() === 'yes';
  }

  // Numeric fields → parse string to number (prevents string concatenation in calculations)
  const NUMERIC_FIELDS = [
    'totalNetWorth', 'liquidNetWorth', 'totalAnnualIncome',
    'dependentsCount', 'yearsEmployed', 'annualIncome',
    'assetBreakdownCash', 'assetBreakdownSecurities', 'assetBreakdownRealEstate',
    'assetBreakdownBusinessEquity', 'assetBreakdownCommodities', 'assetBreakdownCrypto',
    'assetBreakdownOther',
  ];
  if (NUMERIC_FIELDS.includes(lastPart)) {
    const num = parseFloat(value.replace(/[,'\s]/g, ''));
    return isNaN(num) ? value : num;
  }

  // Currency fields → UPPERCASE (form selects use CHF/EUR/USD/GBP)
  const CURRENCY_FIELDS = ['netWorthCurrency', 'incomeCurrency'];
  if (CURRENCY_FIELDS.includes(lastPart)) {
    return value.toUpperCase().trim();
  }

  // Enum-like fields → normalize to lowercase with underscored spaces
  const ENUM_FIELDS = [
    'gender', 'maritalStatus',
    'employmentIndustry',
    // Tax
    'fatcaStatus',
    // PEP / Adverse Media
    'pepType', 'rcaRelationship', 'reputationalRisk',
    // Wealth & Income
    'primaryIncomeType',
    // Business
    'businessType', 'industryRiskLevel',
  ];
  if (ENUM_FIELDS.includes(lastPart)) {
    return value.toLowerCase().replace(/\s+/g, '_');
  }

  // primaryWealthSource — keyword matching for narrative values
  if (lastPart === 'primaryWealthSource') {
    const lower = value.toLowerCase();
    const sourceMap: Record<string, string> = {
      'employment': 'employment', 'salary': 'employment',
      'business': 'business', 'profit': 'business', 'dividend': 'business',
      'inheritance': 'inheritance', 'inherited': 'inheritance',
      'investment': 'investment', 'capital': 'investment',
      'real_estate': 'real_estate', 'real estate': 'real_estate', 'property': 'real_estate', 'rental': 'real_estate',
      'gift': 'gift', 'donation': 'gift',
      'lottery': 'lottery', 'gambling': 'lottery',
      'savings': 'savings', 'saving': 'savings',
      'pension': 'pension', 'retirement': 'pension',
    };
    // Exact match first
    if (sourceMap[lower]) return sourceMap[lower];
    // Keyword search in narrative text
    for (const [kw, enumVal] of Object.entries(sourceMap)) {
      if (lower.includes(kw)) return enumVal;
    }
    return lower.replace(/\s+/g, '_');
  }

  return value;
}

/** Parse common date formats to YYYY-MM-DD */
function parseDateToISO(input: string): string | null {
  // Already ISO: 1939-08-26
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input;

  // DD.MM.YYYY or DD/MM/YYYY
  const dmy = input.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  // MM/DD/YYYY
  const mdy = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) {
    const [, m, d, y] = mdy;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  // Try native Date parse as fallback
  const d = new Date(input);
  if (!isNaN(d.getTime())) {
    return d.toISOString().split('T')[0];
  }

  return null;
}

export const KYCWizardProvider: React.FC<KYCWizardProviderProps> = ({
  children,
  clientId,
  initialData,
  onComplete,
  reviewMode = false,
  embedded = false,
  reviewComments: reviewCommentsProp = [],
  isChangesRequested: isChangesRequestedProp = false,
}) => {
  // TASK-373: Start at step 1 (always, since DocumentUpload step removed)
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [reviewedSteps, setReviewedSteps] = useState<Set<number>>(new Set());

  // Form data state - merge initial data with defaults
  const defaultData: Partial<KYCFormData> = {
    clientId,
    version: 1,
    status: KYCStatus.DRAFT,
    personalInfo: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      nationalities: []
    },
    contactInfo: {
      phonePrimary: '',
      emailPrimary: '',
      residentialAddress: {
        street: '',
        city: '',
        postalCode: '',
        country: ''
      }
    },
    employmentInfo: {
      employmentStatus: 'employed'
    },
    taxInfo: {
      taxResidence: '',
      usPerson: false
    },
    wealthSources: [],
    incomeSources: [],
    documents: [],
    riskAssessment: {
      riskLevel: 'low' as any,
      riskScore: 0,
      riskFactors: [],
      requiresEnhancedDueDiligence: false
    },
    metadata: {
      createdAt: new Date().toISOString(),
      createdBy: 'current-user',
      dataSource: 'manual'
    }
  };

  // Merge initial data with defaults (deep merge for nested objects)
  const mergeFormData = (): Partial<KYCFormData> => {
    if (initialData) {
      const merged: Partial<KYCFormData> = {
        ...defaultData,
        ...initialData,
        personalInfo: {
          ...defaultData.personalInfo,
          ...initialData.personalInfo
        } as any,
        contactInfo: {
          ...defaultData.contactInfo,
          ...initialData.contactInfo,
          residentialAddress: {
            ...defaultData.contactInfo?.residentialAddress,
            ...initialData.contactInfo?.residentialAddress
          } as any
        } as any,
        employmentInfo: {
          ...defaultData.employmentInfo,
          ...initialData.employmentInfo
        } as any,
        taxInfo: {
          ...defaultData.taxInfo,
          ...initialData.taxInfo
        } as any,
        // Personal Background
        personalBackground: {
          ...defaultData.personalBackground,
          ...initialData.personalBackground
        } as any,
        // PEP Status
        pepStatus: {
          ...defaultData.pepStatus,
          ...initialData.pepStatus
        } as any,
        // Merge arrays - prefill data takes precedence over defaults
        adverseMedia: (initialData.adverseMedia || defaultData.adverseMedia || []) as any,
        businessActivities: (initialData.businessActivities || defaultData.businessActivities || []) as any,
        wealthSources: initialData.wealthSources || defaultData.wealthSources,
        incomeSources: initialData.incomeSources || defaultData.incomeSources,
        documents: initialData.documents || defaultData.documents
      };
      return merged;
    }
    return defaultData;
  };

  const [formData, setFormData] = useState<Partial<KYCFormData>>(mergeFormData());

  // Validation state
  const [errors, setErrors] = useState<Record<string, any>>({});

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [enableAutoSave, setEnableAutoSave] = useState(false); // Disabled to prevent creating profiles before submission

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // TASK-261: Prefill version - increments when extraction prefills data
  const [prefillVersion, setPrefillVersion] = useState(0);
  const triggerPrefill = useCallback(() => {
    setPrefillVersion(v => {
      console.log('[KYCWizard] Prefill triggered, version:', v + 1);
      return v + 1;
    });
  }, []);

  // Debounced form data for auto-save
  const debouncedFormData = useDebounce(formData, 3000);

  // Update form data
  const updateFormData = useCallback((section: KYCSection, data: any) => {
    setFormData(prev => {
      // Handle arrays directly (wealthSources, incomeSources, documents, etc.)
      if (Array.isArray(data)) {
        return {
          ...prev,
          [section]: data
        };
      }

      // Handle objects with spreading
      return {
        ...prev,
        [section]: {
          ...((prev[section as keyof Partial<KYCFormData>] as any) || {}),
          ...data
        }
      };
    });
    setIsDirty(true);
  }, []);

  // TASK-322: Update top-level form fields (not nested in a section)
  const updateTopLevelFields = useCallback((fields: Partial<KYCFormData>) => {
    setFormData(prev => ({
      ...prev,
      ...fields
    }));
    setIsDirty(true);
  }, []);

  // Validate step
  const validateStep = useCallback(async (_step: number): Promise<boolean> => {
    // In review mode, skip validation - allow submission with incomplete data
    if (reviewMode) {
      return true;
    }

    // TODO: Implement validation using Zod schemas
    // For now, return true to allow navigation
    return true;
  }, [reviewMode]);

  // Clear errors for a section
  const clearErrors = useCallback((section: KYCSection) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[section];
      return newErrors;
    });
  }, []);

  // Mark step as complete
  const markStepComplete = useCallback((step: number) => {
    setCompletedSteps(prev => new Set([...prev, step]));
  }, []);

  // Mark step as incomplete
  const markStepIncomplete = useCallback((step: number) => {
    setCompletedSteps(prev => {
      const newSet = new Set(prev);
      newSet.delete(step);
      return newSet;
    });
  }, []);

  // Mark step as reviewed (for review mode)
  const markStepReviewed = useCallback((step: number) => {
    setReviewedSteps(prev => {
      const newSet = new Set(prev);
      newSet.add(step);
      return newSet;
    });
  }, []);

  // Check if all steps have been reviewed (for review mode)
  const allStepsReviewed = useCallback((): boolean => {
    // In review mode, check if all steps have been reviewed
    // Skip step 1 (document upload) and step 10 (review & submit)
    const stepsToReview = KYC_STEPS.filter(s => s.id !== 1 && s.id !== 10).map(s => s.id);
    return stepsToReview.every(step => reviewedSteps.has(step));
  }, [reviewedSteps]);

  // Check if can navigate to step
  const canNavigateToStep = useCallback((step: number): boolean => {
    // Can always go to previous steps or current step
    if (step <= currentStep) return true;

    // Can only go to next step if current step is completed
    // For now, allow free navigation during development
    return true;
  }, [currentStep]);

  // Save progress to localStorage (internal helper for navigation)
  const saveToLocalStorage = useCallback((nextStep: number, updatedCompletedSteps: Set<number>) => {
    try {
      const storageKey = `kyc_draft_${clientId}`;
      localStorage.setItem(storageKey, JSON.stringify({
        formData,
        completedSteps: Array.from(updatedCompletedSteps),
        currentStep: nextStep,
        savedAt: new Date().toISOString()
      }));
      setLastSavedAt(new Date());
      setIsDirty(false);
    } catch (error) {
      console.error('[KYCWizard] Failed to save to localStorage:', error);
    }
  }, [clientId, formData]);

  // Navigation functions
  const goToNextStep = useCallback(async () => {
    const isValid = await validateStep(currentStep);
    if (isValid) {
      // In review mode, mark step as reviewed instead of completed
      let updatedCompletedSteps = completedSteps;
      if (reviewMode) {
        markStepReviewed(currentStep);
      } else {
        updatedCompletedSteps = new Set([...completedSteps, currentStep]);
        setCompletedSteps(updatedCompletedSteps);
      }

      const nextStep = currentStep < KYC_STEPS.length ? currentStep + 1 : currentStep;

      // Always save progress to localStorage when navigating
      saveToLocalStorage(nextStep, updatedCompletedSteps);

      if (currentStep < KYC_STEPS.length) {
        setCurrentStep(nextStep);
      }
    }
  }, [currentStep, validateStep, completedSteps, markStepReviewed, reviewMode, saveToLocalStorage]);

  const goToPreviousStep = useCallback(() => {
    if (currentStep > 1) {
      saveToLocalStorage(currentStep - 1, completedSteps);
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, completedSteps, saveToLocalStorage]);

  const goToStep = useCallback((step: number) => {
    if (canNavigateToStep(step)) {
      saveToLocalStorage(step, completedSteps);
      setCurrentStep(step);
    }
  }, [canNavigateToStep, completedSteps, saveToLocalStorage]);

  // Save progress (only to localStorage until final submission)
  const saveProgress = useCallback(async (showNotification = true) => {
    setIsSaving(true);
    try {
      // Only save to localStorage for drafts
      // Profile will be created on final submission
      const storageKey = `kyc_draft_${clientId}`;
      localStorage.setItem(storageKey, JSON.stringify({
        formData,
        completedSteps: Array.from(completedSteps),
        currentStep,
        savedAt: new Date().toISOString()
      }));

      setLastSavedAt(new Date());
      setIsDirty(false);

      if (showNotification) {
        toast.success('Draft saved locally', {
          duration: 2000,
          position: 'bottom-right'
        });
      }
    } catch (error) {
      console.error('Failed to save draft:', error);
      if (showNotification) {
        toast.error('Failed to save draft');
      }
    } finally {
      setIsSaving(false);
    }
  }, [clientId, formData, completedSteps, currentStep]);

  // Auto-save effect
  useEffect(() => {
    if (enableAutoSave && isDirty && !isSaving) {
      saveProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFormData, enableAutoSave, isDirty, isSaving]);

  // Load saved progress on mount
  useEffect(() => {
    const loadDraft = async () => {
      if (initialData) return;

      // Only load from localStorage (profiles are created on submission only)
      const storageKey = `kyc_draft_${clientId}`;
      const savedData = localStorage.getItem(storageKey);

      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          setFormData(parsed.formData);
          setCompletedSteps(new Set(parsed.completedSteps));
          setCurrentStep(parsed.currentStep);
          toast.info('Draft loaded from local storage', {
            duration: 3000,
            position: 'bottom-right'
          });
        } catch (error) {
          console.error('Failed to load saved draft:', error);
        }
      }
    };

    loadDraft();
  }, [clientId, initialData]);

  // Submit form
  const submitForm = useCallback(async () => {
    setIsSubmitting(true);
    try {
      // In review mode, check if all steps have been reviewed
      if (reviewMode) {
        if (!allStepsReviewed()) {
          toast.error('Please review all sections before submitting', {
            description: 'Navigate through each step to confirm you have reviewed the extracted data',
            duration: 5000
          });
          setIsSubmitting(false);
          return;
        }
        // Skip field validation in review mode
      } else {
        // Validate all steps (only in normal mode)
        for (let i = 1; i <= KYC_STEPS.length - 1; i++) {
          const isValid = await validateStep(i);
          if (!isValid) {
            setCurrentStep(i);
            toast.error(`Please complete step ${i}: ${KYC_STEPS[i - 1].name}`);
            setIsSubmitting(false);
            return;
          }
        }
      }

      // Check if profile is already submitted — save data only, skip re-submit
      const alreadySubmitted = formData.status === KYCStatus.SUBMITTED
        || formData.status === KYCStatus.IN_REVIEW;

      if (alreadySubmitted) {
        // Save updated data without changing status
        const updatedData: KYCFormData = {
          ...formData,
          metadata: {
            ...formData.metadata,
            updatedAt: new Date().toISOString(),
          }
        } as KYCFormData;

        await kycService.saveDraft(updatedData);

        if (onComplete) {
          await onComplete(updatedData);
        }

        toast.success('KYC form updated successfully!', {
          duration: 5000,
          position: 'bottom-right'
        });
      } else {
        // First submission — change status to submitted
        const submittedData: KYCFormData = {
          ...formData,
          status: KYCStatus.SUBMITTED,
          metadata: {
            ...formData.metadata,
            updatedAt: new Date().toISOString(),
            completedAt: new Date().toISOString()
          }
        } as KYCFormData;

        await kycService.submitForReview(submittedData);

        if (onComplete) {
          await onComplete(submittedData);
        }

        // Clear localStorage draft and progress
        const draftKey = `kyc_draft_${clientId}`;
        const progressKey = `kyc_progress_${clientId}`;
        localStorage.removeItem(draftKey);
        localStorage.removeItem(progressKey);

        toast.success('KYC form submitted successfully!', {
          duration: 5000,
          position: 'bottom-right'
        });
      }

      // Reload workspace to reflect the new status
      if (embedded) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to submit form:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, clientId, validateStep, onComplete, reviewMode, allStepsReviewed]);

  // Reset wizard
  const resetWizard = useCallback(() => {
    setCurrentStep(1);
    setCompletedSteps(new Set());
    setFormData({
      clientId,
      version: 1,
      status: KYCStatus.DRAFT,
      personalInfo: {
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        nationalities: []
      },
      contactInfo: {
        phonePrimary: '',
        emailPrimary: '',
        residentialAddress: {
          street: '',
          city: '',
          postalCode: '',
          country: ''
        }
      },
      employmentInfo: {
        employmentStatus: 'employed'
      },
      taxInfo: {
        taxResidence: '',
        usPerson: false
      },
      wealthSources: [],
      incomeSources: [],
      documents: [],
      riskAssessment: {
        riskLevel: 'low' as any,
        riskScore: 0,
        riskFactors: [],
        requiresEnhancedDueDiligence: false
      },
      metadata: {
        createdAt: new Date().toISOString(),
        createdBy: 'current-user',
        dataSource: 'manual'
      }
    });
    setErrors({});
    setIsDirty(false);
    setLastSavedAt(null);
  }, [clientId]);

  // TASK-373: Apply extraction proposal to form data
  // skipPrefill: when true, caller is responsible for calling triggerPrefill() after batch
  const applyExtractionProposal = useCallback((fieldPath: string, value: any, skipPrefill = false) => {
    // Normalize value based on field type
    value = normalizeFieldValue(fieldPath, value);
    const parts = fieldPath.split('.');

    // Top-level fields (e.g. "totalNetWorth", "liquidNetWorth")
    if (parts.length === 1) {
      setFormData(prev => ({ ...prev, [parts[0]]: value }));
      setIsDirty(true);
      if (!skipPrefill) triggerPrefill();
      return;
    }

    // Handle staging fields for array items (institutions, certifications, businesses)
    const lastPart = parts[parts.length - 1];
    const stagingConfigs: Record<string, { section: string; arrayKey: string; nameKey: string; keyMap: Record<string, string> }> = {
      '_institution_': { section: 'personalBackground', arrayKey: 'institutionsAttended', nameKey: 'name', keyMap: { name: 'name', degree: 'degree', year: 'graduationYear' } },
      '_certification_': { section: 'personalBackground', arrayKey: 'professionalCertifications', nameKey: 'name', keyMap: { name: 'name', issuer: 'issuer', year: 'year' } },
      '_business_': { section: 'businessActivities', arrayKey: 'businesses', nameKey: 'businessName', keyMap: { name: 'businessName', type: 'businessType', number: 'registrationNumber', country: 'registrationCountry', industry: 'industry', percentage: 'ownershipPercentage', revenue: 'annualRevenue', cross_border: 'crossBorderTransactions', cash_intensive: 'cashIntensive' } },
      '_board_': { section: 'adverseMedia', arrayKey: 'boardPositions', nameKey: 'company', keyMap: { company: 'company', position: 'position', country: 'country', current: 'current', publicly_traded: 'isPubliclyTraded', isin: 'isin' } },
      '_ws_': { section: 'wealthSources', arrayKey: 'sources', nameKey: 'sourceType', keyMap: { type: 'sourceType', percentage: 'percentage', amount: 'amount', description: 'description', start_date: 'acquisitionStartDate', end_date: 'acquisitionEndDate', ongoing: 'isOngoing', asset_type: 'assetType', asset_location: 'assetLocation', liquidity_level: 'liquidityLevel' } },
      '_is_': { section: 'incomeSources', arrayKey: 'sources', nameKey: 'incomeType', keyMap: { type: 'incomeType', name: 'sourceName', description: 'incomeDescription', amount: 'annualAmount', currency: 'currency', frequency: 'frequency', country: 'sourceCountry', start_date: 'startDate', ongoing: 'isOngoing', stability: 'stabilityRating', continuity: 'expectedContinuity' } },
    };

    for (const [prefix, config] of Object.entries(stagingConfigs)) {
      if (!lastPart.startsWith(prefix)) continue;
      const subField = lastPart.replace(prefix, '');
      const objKey = config.keyMap[subField] || subField;
      const numericKeys = ['graduationYear', 'year', 'ownershipPercentage', 'annualRevenue', 'percentage', 'amount', 'annualAmount'];
      const booleanKeys = ['current', 'isPubliclyTraded', 'crossBorderTransactions', 'cashIntensive', 'isOngoing'];
      const enumKeys = ['industry', 'businessType', 'sourceType', 'assetType', 'liquidityLevel', 'incomeType', 'frequency', 'stabilityRating', 'expectedContinuity'];
      // Synonym maps for staging enum fields where LLM uses non-standard values
      const STAGING_SYNONYMS: Record<string, Record<string, string>> = {
        incomeType: { employment: 'salary', wages: 'salary', dividend: 'dividends', participation: 'dividends', securities: 'investment', capital_gains: 'investment', interest: 'investment', rent: 'rental', rental_income: 'rental', retirement: 'pension', consulting: 'business', freelance: 'business' },
        industry: { financial_services: 'banking', fintech: 'technology', pharma: 'healthcare', pharmaceutical: 'healthcare', it: 'technology', software: 'technology', law: 'legal', defense: 'arms', defence: 'arms', mining: 'precious_metals', ngo: 'nonprofit', charity: 'nonprofit' },
      };
      let finalVal: any = value;
      if (numericKeys.includes(objKey)) {
        const parsed = parseFloat(String(value));
        if (isNaN(parsed)) {
          console.warn(`[KYCWizard] Skipping non-numeric value for ${objKey}:`, value);
          return; // Don't apply non-numeric values to numeric fields
        }
        finalVal = parsed;
      } else if (booleanKeys.includes(objKey)) finalVal = value === true || value === 'true' || value === '1' || value === 'yes';
      else if (enumKeys.includes(objKey) && typeof value === 'string') {
        finalVal = value.toLowerCase().replace(/\s+/g, '_');
        // Apply synonym mapping if available
        if (STAGING_SYNONYMS[objKey]?.[finalVal]) finalVal = STAGING_SYNONYMS[objKey][finalVal];
      }

      // Derive the entry starter for THIS config: the subField whose keyMap value === nameKey
      // e.g. _is_ → nameKey="incomeType", keyMap has type→incomeType, so starter is "type"
      // e.g. _business_ → nameKey="businessName", keyMap has name→businessName, so starter is "name"
      const entryStarter = Object.keys(config.keyMap).find(k => config.keyMap[k] === config.nameKey);

      setFormData(prev => {
        const sectionData = (prev as any)[config.section] || {};
        const arr: any[] = [...(sectionData[config.arrayKey] || [])];

        if (subField === entryStarter) {
          // Entry starter always creates a new array entry.
          // No dedup by type — multiple sources can share the same type
          // (e.g. two dividend income sources or two investment wealth sources).
          arr.push({ [objKey]: finalVal });
        } else if (arr.length > 0) {
          arr[arr.length - 1] = { ...arr[arr.length - 1], [objKey]: finalVal };
        } else {
          arr.push({ [objKey]: finalVal });
        }
        // For business activities, ensure toggle is set
        const extra = config.section === 'businessActivities' ? { hasBusinessActivities: true } : {};
        return { ...prev, [config.section]: { ...sectionData, ...extra, [config.arrayKey]: arr } };
      });
      setIsDirty(true);
      if (!skipPrefill) triggerPrefill();
      return;
    }

    // Nested: "section.field" e.g. "personalInfo.firstName"
    const section = parts[0] as KYCSection;
    const field = parts.slice(1).join('.');

    setFormData(prev => {
      const sectionData = (prev as any)[section] || {};
      // Simple nested: section.field
      if (parts.length === 2) {
        return {
          ...prev,
          [section]: { ...sectionData, [field]: value }
        };
      }
      // Deep nested: section.sub.field
      const nested = { ...sectionData };
      let current: any = nested;
      for (let i = 1; i < parts.length - 1; i++) {
        current[parts[i]] = { ...(current[parts[i]] || {}) };
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
      return { ...prev, [section]: nested };
    });
    setIsDirty(true);
    if (!skipPrefill) triggerPrefill();
  }, [triggerPrefill]);

  const contextValue: KYCWizardContextType = {
    clientId,  // TASK-220: Exposed for document extraction
    currentStep,
    totalSteps: KYC_STEPS.length,
    setCurrentStep,
    formData,
    updateFormData,
    updateTopLevelFields,  // TASK-322: For top-level field updates
    prefillVersion,  // TASK-261: For step re-initialization
    triggerPrefill,  // TASK-261: Call after extraction prefill
    errors,
    validateStep,
    clearErrors,
    completedSteps,
    markStepComplete,
    markStepIncomplete,
    canNavigateToStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    isSaving,
    lastSavedAt,
    isDirty,
    saveProgress,
    enableAutoSave,
    setEnableAutoSave,
    isSubmitting,
    submitForm,
    resetWizard,
    reviewMode,
    reviewedSteps,
    markStepReviewed,
    allStepsReviewed,
    applyExtractionProposal,  // TASK-373
    embedded,  // TASK-373
    reviewComments: reviewCommentsProp,
    isChangesRequested: isChangesRequestedProp,
  };

  // TASK-716: expose live dirty state for the focus popup's close guard —
  // Esc/✕/Back on a dirty form must prompt, never silently discard.
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const setBridgeIsDirty = useKYCBridge((s) => s.setIsDirty);
  useEffect(() => {
    setBridgeIsDirty(() => isDirtyRef.current);
    return () => setBridgeIsDirty(null);
  }, [setBridgeIsDirty]);

  // Expose goToStep via store for cross-panel navigation
  // (e.g. COReviewTab in left panel navigating the wizard in right panel)
  const setGoToStep = useKYCBridge((s) => s.setGoToStep);
  useEffect(() => {
    setGoToStep(goToStep);
    return () => { setGoToStep(null); };
  }, [goToStep, setGoToStep]);

  // TASK-371: Sync current step to the KYC chat store so the copilot
  // knows which section the user is viewing.
  const setChatSection = useKYCBridge((s) => s.setCurrentSection);
  const setFormDataSnapshot = useKYCBridge((s) => s.setFormDataSnapshot);
  useEffect(() => {
    const step = KYC_STEPS[currentStep - 1];
    if (step) {
      setChatSection(`Step ${step.id}: ${step.name} (${step.section})`);
    }
  }, [currentStep, setChatSection]);

  // Publish full form data snapshot so the AI copilot is aware of every
  // section the user has filled — not just the one currently displayed.
  // _currentSection tells the backend which step is active.
  useEffect(() => {
    const step = KYC_STEPS[currentStep - 1];
    if (!step) return;
    const snapshot: Record<string, unknown> = {
      _currentSection: step.section,
      ...(formData as Record<string, unknown>),
    };
    setFormDataSnapshot(snapshot);
  }, [currentStep, formData, setFormDataSnapshot]);

  // Register a live reader so KYCChatPanel can read the latest formData
  // at send time, bypassing the useEffect render-cycle lag.
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const setGetFormDataSnapshot = useKYCBridge((s) => s.setGetFormDataSnapshot);
  const currentStepRef = useRef(currentStep);
  currentStepRef.current = currentStep;
  useEffect(() => {
    const getSnapshot = (): Record<string, unknown> => {
      const step = KYC_STEPS[currentStepRef.current - 1];
      return {
        _currentSection: step?.section || 'unknown',
        ...(formDataRef.current as Record<string, unknown>),
      };
    };
    setGetFormDataSnapshot(getSnapshot);
    return () => { setGetFormDataSnapshot(null); };
  }, [setGetFormDataSnapshot]);

  // TASK-371: Subscribe to the kycChatStore's appliedBatch signal.
  // When the user clicks Apply / Apply All, the store fires a batch
  // which we consume here to update formData for every item in one pass.
  const appliedBatch = useKYCBridge((s) => s.appliedBatch);
  const lastAppliedSeqRef = useRef(0);

  useEffect(() => {
    if (!appliedBatch || appliedBatch.seq <= lastAppliedSeqRef.current) return;
    lastAppliedSeqRef.current = appliedBatch.seq;
    // Apply all items with skipPrefill=true, then trigger prefill once at the end
    for (const item of appliedBatch.items) {
      applyExtractionProposal(item.fieldPath, item.value, true);
    }
    triggerPrefill();
  }, [appliedBatch, applyExtractionProposal, triggerPrefill]);

  return (
    <KYCWizardContext.Provider value={contextValue}>
      {children}
    </KYCWizardContext.Provider>
  );
};

// Export step configuration for use in other components
export { KYC_STEPS };